import { useEffect, useState } from "react";
import { motion } from "@/lib/animations";
import { durations, easings, delays } from "@/lib/animations";

interface PaceChartCardProps {
  averagePace: string | null;
}

export function PaceChartCard({ averagePace }: PaceChartCardProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Path for the chart line
  const linePath =
    "M0,100 C50,110 80,60 150,50 C220,40 280,80 340,30 C370,5 390,10 400,20";
  // Path for the filled area
  const areaPath =
    "M0,150 L0,100 C50,110 80,60 150,50 C220,40 280,80 340,30 C370,5 390,10 400,20 L400,150 Z";

  return (
    <div className="rounded-4xl bg-gradient-to-r from-[#112411] to-[#0c1c0c] border border-primary/20 p-0 relative overflow-hidden h-36">
      {/* Background Chart */}
      <div className="absolute inset-0 opacity-40">
        <svg
          className="w-full h-full"
          preserveAspectRatio="none"
          viewBox="0 0 400 150"
        >
          <defs>
            <linearGradient id="paceGradient" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#25f478" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#25f478" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Animated fill area */}
          <motion.path
            d={areaPath}
            fill="url(#paceGradient)"
            initial={{ opacity: 0 }}
            animate={{ opacity: mounted ? 1 : 0 }}
            transition={{
              duration: durations.progress,
              ease: easings.easeOut,
              delay: delays.medium + 0.3,
            }}
          />

          {/* Animated line */}
          <motion.path
            d={linePath}
            fill="none"
            stroke="#25f478"
            strokeLinecap="round"
            strokeWidth="3"
            className="drop-shadow-[0_0_6px_rgba(37,244,120,0.8)]"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{
              pathLength: mounted ? 1 : 0,
              opacity: mounted ? 1 : 0,
            }}
            transition={{
              pathLength: {
                duration: durations.progress,
                ease: easings.easeOut,
                delay: delays.medium,
              },
              opacity: {
                duration: 0.2,
                delay: delays.medium,
              },
            }}
          />

          {/* Animated dot at the end */}
          <motion.circle
            cx="340"
            cy="30"
            fill="#fff"
            r="4"
            className="drop-shadow-lg"
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: mounted ? 1 : 0,
              opacity: mounted ? 1 : 0,
            }}
            transition={{
              duration: 0.3,
              ease: easings.easeOut,
              delay: delays.medium + durations.progress,
            }}
          />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 p-5 h-full flex flex-col justify-between">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-full bg-primary/10">
              <span className="material-symbols-outlined text-primary text-sm">
                speed
              </span>
            </div>
            <span className="text-sm font-semibold text-white/70">
              Average Pace
            </span>
          </div>
          <motion.div
            className="flex items-center gap-1 bg-primary/20 px-2 py-1 rounded-full border border-primary/20 backdrop-blur-md"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: mounted ? 1 : 0, scale: mounted ? 1 : 0.8 }}
            transition={{
              duration: 0.4,
              ease: easings.easeOut,
              delay: delays.medium + durations.progress + 0.2,
            }}
          >
            <span className="material-symbols-outlined text-primary text-xs">
              trending_up
            </span>
            <span className="text-xs font-bold text-primary">Great pace!</span>
          </motion.div>
        </div>
        <motion.div
          className="flex items-baseline gap-1 mt-auto"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: mounted ? 1 : 0, y: mounted ? 0 : 10 }}
          transition={{
            duration: 0.5,
            ease: easings.easeOut,
            delay: delays.medium,
          }}
        >
          <span className="text-4xl font-extrabold text-white text-glow-sm">
            {averagePace || "N/A"}
          </span>
          <span className="text-lg font-medium text-white/60">/km</span>
        </motion.div>
      </div>
    </div>
  );
}
